<template>
  <div style="background-color:black; height:100vh;">
    <v-container fluid fill-height>
      <!-- Back Button -->
      <v-btn icon class="back-button" @click="confirmBackNavigation">
        <v-icon>mdi-arrow-left</v-icon>
      </v-btn>

      <!-- Page Header -->
      <v-row class="pa-7" justify="center">
        <h2 class="red--text">EDIT SERIAL NUMBER</h2>
      </v-row>

      <!-- Form Card -->
      <v-row class="fill-height pa-0 ma-0" justify="center" align="start">
        <v-col cols="12" class="pa-0">
          <v-card elevation="0" class="edit-serial-card">
            <v-card-text>

              <!-- Loading Overlay -->
              <v-overlay :value="loading">
                <v-progress-circular indeterminate size="64" />
              </v-overlay>

              <v-row>
                <h2 class="red--text mx-12" style="margin-bottom: 30px;">
                  SERIAL NUMBER DETAILS
                </h2>
                <v-spacer />
              </v-row>

              <v-form ref="form" v-model="valid" lazy-validation>
                <!-- Row 1: Certificate, Serial#, Date, Customer, SO/INV -->
                <v-row>
                  <v-col cols="12" md="2">
                    <v-text-field
                      v-model="serial.primaryKey"
                      label="Certificate Number"
                      dense outlined readonly
                      prepend-inner-icon="mdi-key"
                      class="montserrat-input"
                      :rules="[rules.required, rules.qcPrefix]"
                    />
                  </v-col>
                  <v-col cols="12" md="2">
                    <v-text-field
                      v-model="serial.linkKey"
                      label="Serial Number"
                      dense outlined
                      prepend-inner-icon="mdi-link"
                      class="montserrat-input"
                      :rules="[rules.required, rules.qcPrefix]"
                      @input="ensureQCPrefix('linkKey')"
                    />
                  </v-col>
                  <v-col cols="12" md="2">
                    <v-menu
                      ref="dateMenu"
                      v-model="dateMenu"
                      :close-on-content-click="false"
                      transition="scale-transition"
                      offset-y
                      min-width="auto"
                    >
                      <template #activator="{ on, attrs }">
                        <v-text-field
                          v-model="serial.date"
                          label="Date"
                          readonly dense outlined
                          prepend-inner-icon="mdi-calendar"
                          v-bind="attrs" v-on="on"
                          :rules="[rules.required]"
                        />
                      </template>
                      <v-date-picker
                        v-model="serial.date"
                        @input="dateMenu = false"
                      />
                    </v-menu>
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-autocomplete
                      v-model="serial.customer"
                      :items="customerOptions"
                      item-text="companyName"
                      item-value="companyName"
                      label="Customer"
                      dense outlined
                      prepend-inner-icon="mdi-account-group"
                      :rules="[rules.required]"
                      :loading="customerLoading"
                      :disabled="!customerOptions.length"
                      :hint="customerOptions.length ? '' : 'No customers available'"
                      persistent-hint
                    />
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-text-field
                      v-model="serial.docNumber"
                      label="SO/INV Number"
                      dense outlined
                      prepend-inner-icon="mdi-file-document"
                      class="montserrat-input"
                      :rules="[rules.required]"
                    />
                  </v-col>
                </v-row>

                <!-- Row 2: Code, Elastomers, IBS, IBR, OBS (opt), OBR (opt) -->
                <v-row>
                  <v-col cols="12" md="2">
                    <v-text-field
                      v-model="serial.code"
                      label="Type/Code"
                      dense outlined
                      prepend-inner-icon="mdi-cog"
                      class="montserrat-input"
                      :rules="[rules.required]"
                    />
                  </v-col>
                  <v-col cols="12" md="2">
                    <v-text-field
                      v-model="serial.elastomers"
                      label="Elastomers"
                      dense outlined
                      prepend-inner-icon="mdi-cog"
                      class="montserrat-input"
                      :rules="[rules.required]"
                    />
                  </v-col>
                  <v-col cols="12" md="2">
                    <v-text-field
                      v-model="serial.ibs"
                      label="IBS Face Material"
                      dense outlined
                      prepend-inner-icon="mdi-cog-outline"
                      class="montserrat-input"
                      :rules="[rules.required]"
                    />
                  </v-col>
                  <v-col cols="12" md="2">
                    <v-text-field
                      v-model="serial.ibr"
                      label="IBR Face Material"
                      dense outlined
                      prepend-inner-icon="mdi-cog-outline"
                      class="montserrat-input"
                      :rules="[rules.required]"
                    />
                  </v-col>
                  <v-col cols="12" md="2">
                    <v-text-field
                      v-model="serial.obs"
                      label="OBS Face Material"
                      dense outlined
                      prepend-inner-icon="mdi-cog-outline"
                      class="montserrat-input"
                    />
                  </v-col>
                  <v-col cols="12" md="2">
                    <v-text-field
                      v-model="serial.obr"
                      label="OBR Face Material"
                      dense outlined
                      prepend-inner-icon="mdi-cog-outline"
                      class="montserrat-input"
                    />
                  </v-col>
                </v-row>

                <!-- Row 3: Shaft Size -->
                <v-row>
                  <v-col cols="12" md="3">
                    <v-text-field
                      v-model="serial.shaftSize"
                      label="Shaft Size"
                      dense outlined
                      prepend-inner-icon="mdi-ruler"
                      class="montserrat-input"
                      :rules="[rules.required]"
                    />
                  </v-col>
                </v-row>

                <!-- Row 4: Notes -->
                <v-row>
                  <v-col cols="12">
                    <v-textarea
                      v-model="serial.notes"
                      label="Notes"
                      auto-grow rows="2"
                      dense outlined
                      prepend-inner-icon="mdi-note"
                      class="montserrat-input"
                    />
                  </v-col>
                </v-row>

                <!-- Save Button -->
                <v-row justify="center" class="mt-8">
                  <v-btn
                    dark large width="300px"
                    @click="confirmSubmit"
                    class="montserrat-button pa-0"
                  >
                    <v-icon left>mdi-content-save</v-icon>Save
                  </v-btn>
                </v-row>
              </v-form>

            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- Confirmation Dialog -->
    <v-dialog v-model="confirmationDialog.visible" max-width="400">
      <v-card class="confirmation-dialog-card">
        <v-card-title class="headline pa-4">
          {{ confirmationDialog.title }}
        </v-card-title>
        <v-card-text>
          {{ confirmationDialog.message }}
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn color="primary" text @click="confirmationDialog.confirm">
            <v-icon left>mdi-check</v-icon>Yes
          </v-btn>
          <v-btn color="grey" text @click="confirmationDialog.visible = false">
            <v-icon left>mdi-close</v-icon>No
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Success Snackbar -->
    <v-snackbar v-model="successSnackbar" color="success" timeout="3000">
      {{ successMessage }}
      <v-btn color="white" text @click="successSnackbar = false">Close</v-btn>
    </v-snackbar>

    <!-- Error Snackbar -->
    <v-snackbar v-model="errorSnackbar" color="error" timeout="3000">
      {{ errorMessage }}
      <v-btn color="white" text @click="errorSnackbar = false">Close</v-btn>
    </v-snackbar>
  </div>
</template>

<script>
import {
  getFirestore,
  collection,
  getDocs,
  doc,
  getDoc,
  updateDoc
} from "firebase/firestore";
import { firebaseApp, auth } from "@/firebaseConfig";

const db = getFirestore(firebaseApp);

export default {
  name: "EditSerialNumber",
  data() {
    return {
      loading: false,
      customerLoading: false,
      valid: false,
      dateMenu: false,
      serial: {
        id: "",
        primaryKey: "",
        linkKey: "",
        date: "",
        customer: "",
        docNumber: "",
        code: "",
        elastomers: "",
        ibs: "",
        ibr: "",
        obs: "",
        obr: "",
        shaftSize: "",
        notes: ""
      },
      customerOptions: [],
      confirmationDialog: {
        visible: false,
        title: "",
        message: "",
        confirm: () => {}
      },
      successSnackbar: false,
      successMessage: "",
      errorSnackbar: false,
      errorMessage: "",
      rules: {
        required: v => !!v || "Required.",
        qcPrefix: v => (v && v.startsWith("NSS")) || "Must start with NSS"
      }
    };
  },
  async created() {
    this.loading = true;
    try {
      const user = auth.currentUser;
      if (!user) throw new Error("Not logged in");

      // Check edit permission
      const userSnap = await getDoc(doc(db, "users", user.uid));
      const allowed = userSnap.exists() && (userSnap.data().serial?.Edit_Details || false);
      if (!allowed) {
        this.errorMessage = "You do not have permission to edit.";
        this.errorSnackbar = true;
        this.$router.push({ name: "AllSerialNumbers" });
        return;
      }

      // Load serial and customers
      await Promise.all([
        this.fetchSerialNumber(),
        this.fetchCustomerOptions()
      ]);
    } catch (e) {
      console.error(e);
      this.errorMessage = "Initialization failed.";
      this.errorSnackbar = true;
      this.$router.push({ name: "AllSerialNumbers" });
    } finally {
      this.loading = false;
    }
  },
  methods: {
    async fetchSerialNumber() {
      const serialId = localStorage.getItem("selectedSerialId");
      if (!serialId) {
        throw new Error("No serial selected");
      }
      const snap = await getDoc(doc(db, "SerialNumber", serialId));
      if (!snap.exists()) {
        throw new Error("Serial not found");
      }
      this.serial = {
        id: snap.id,
        ...snap.data(),
        shaftSize: snap.data().shaftSize || "",
        notes: snap.data().notes || ""
      };
    },
    async fetchCustomerOptions() {
      this.customerLoading = true;
      try {
        const snap = await getDocs(collection(db, "customers"));
        this.customerOptions = snap.docs
          .map(d => ({
            docId: d.id,
            companyName: d.data()["Company Name"] || ""
          }))
          .filter(c => c.companyName)
          .sort((a, b) => a.companyName.localeCompare(b.companyName));

        if (!this.customerOptions.length) {
          this.errorMessage = "No customers available.";
          this.errorSnackbar = true;
        }
      } finally {
        this.customerLoading = false;
      }
    },
    ensureQCPrefix(field) {
      if (this.serial[field] && !this.serial[field].startsWith("NSS")) {
        this.serial[field] = "NSS" + this.serial[field];
      }
    },
    confirmSubmit() {
      if (!this.$refs.form.validate()) {
        this.errorMessage = "Resolve validation errors.";
        this.errorSnackbar = true;
        return;
      }
      this.confirmationDialog = {
        visible: true,
        title: "Confirm Save",
        message: `Save changes to ${this.serial.primaryKey}?`,
        confirm: this.submitForm
      };
    },
    async submitForm() {
      this.loading = true;
      try {
        await updateDoc(doc(db, "SerialNumber", this.serial.id), {
          linkKey: this.serial.linkKey,
          date: this.serial.date,
          customer: this.serial.customer,
          docNumber: this.serial.docNumber,
          code: this.serial.code,
          elastomers: this.serial.elastomers,
          ibs: this.serial.ibs,
          ibr: this.serial.ibr,
          obs: this.serial.obs || "",
          obr: this.serial.obr || "",
          shaftSize: this.serial.shaftSize,
          notes: this.serial.notes || ""
        });
        this.successMessage = `${this.serial.primaryKey} updated!`;
        this.successSnackbar = true;
        this.$router.push({ name: "AllSerialNumbers" });
      } catch (e) {
        console.error(e);
        this.errorMessage = "Update failed.";
        this.errorSnackbar = true;
      } finally {
        this.loading = false;
        this.confirmationDialog.visible = false;
      }
    },
    confirmBackNavigation() {
      this.confirmationDialog = {
        visible: true,
        title: "Discard Changes?",
        message: "Unsaved changes will be lost.",
        confirm: this.goBack
      };
    },
    goBack() {
      this.confirmationDialog.visible = false;
      this.$router.push({ name: "AllSerialNumbers" });
    }
  }
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Montserrat:wght@400;700&display=swap");

.edit-serial-card {
  box-shadow: 10px 10px 20px #1b1c1e, -10px -10px 20px #c4242b;
  border-radius: 20px;
  overflow: hidden;
  margin-top: 20px;
  padding: 20px;
  transition: all 0.3s ease-in-out;
}

.montserrat-input,
.montserrat-button {
  font-family: "Montserrat", sans-serif;
}

.back-button {
  color: white;
  border-radius: 50%;
  position: absolute;
  top: 16px;
  left: 16px;
  transition: transform 0.3s ease-in-out;
}

.back-button:hover {
  transform: scale(1.1);
}

.confirmation-dialog-card {
  border-radius: 8px;
  background-color: white;
}

.confirmation-dialog-card .headline {
  background-color: #c4242b;
  color: white;
  font-family: "Montserrat", sans-serif;
}

.v-text-field--outlined >>> fieldset,
.v-autocomplete--outlined >>> fieldset {
  border-color: #555;
}

.v-text-field--outlined:hover >>> fieldset,
.v-autocomplete--outlined:hover >>> fieldset {
  border-color: #c4242b;
}

.montserrat-button {
  font-weight: 700;
  letter-spacing: 1px;
}
</style>
